import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Search, Building2, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import PageHero from "@/components/site/PageHero";
import Reveal from "@/components/site/Reveal";
import { useSEO } from "@/lib/seo";
import { useSiteSettings } from "@/lib/settings";
import { api } from "@/lib/api";
import { getIcon } from "@/lib/icon-map";
import { SERVICES_DEFAULTS, pick } from "@/lib/cms-defaults";

// H2 title size class — bumped by ~2px from previous text-3xl / sm:text-4xl
const H2_TITLE = "font-heading text-[32px] font-semibold text-slate-900 sm:text-[38px] leading-tight";

const DEPARTMENTS_FALLBACK = [
  { icon: "FlaskConical", name: "Formulation R&D" },
  { icon: "Microscope", name: "Clinical Research" },
  { icon: "ShieldCheck", name: "Regulatory Affairs" },
  { icon: "Pill", name: "API / Generics" },
  { icon: "Stethoscope", name: "Medical Affairs" },
  { icon: "BarChart3", name: "Sales & Marketing" },
  { icon: "Building2", name: "Manufacturing" },
  { icon: "Users", name: "Quality / QA-QC" },
];

const SERVICES_FALLBACK = [
  {
    title: "Senior Management",
    tagline: "Vision, strategy & long-term growth",
    description: "Senior Management comprises the highest level of leadership within an organisation. These professionals are responsible for defining the company's vision, business strategy, operational excellence, regulatory compliance, financial performance, innovation, and long-term growth.",
    roles: "CEO, MD, COO, CFO, CHRO, Director, Executive Director, Sr.VP, Head, AVP, GM, Sr.GM",
    icon: "Crown",
  },
  {
    title: "Middle Management",
    tagline: "The bridge between strategy and execution",
    description: "Middle Management acts as the bridge between senior leadership and operational teams. These professionals convert strategic objectives into day-to-day execution.",
    roles: "DGM, AGM, Sr.Manager, Manager",
    icon: "Users",
  },
  {
    title: "Junior & Entry-level Professionals",
    tagline: "The foundation of every pharma organisation",
    description: "Junior & Entry-Level Professionals are the foundation of every pharmaceutical organisation. These individuals perform operational, technical, analytical, and support functions.",
    roles: "Sr.Executive, Executive, Sr.Officers, Officers, Associates, Analyst, Trainee",
    icon: "Sparkles",
  },
];

export default function Services() {
  const { settings } = useSiteSettings();
  const c = settings?.services_content || {};
  const t = (k) => pick(c, SERVICES_DEFAULTS, k);
  useSEO({
    title: "Services",
    description: t("hero_body"),
    pageKey: "services",
  });

  const [departments, setDepartments] = useState(DEPARTMENTS_FALLBACK);
  const [services, setServices] = useState(SERVICES_FALLBACK);

  useEffect(() => {
    api.get("/departments", { params: { with_counts: true } })
      .then((r) => { const arr = Array.isArray(r.data) ? r.data : []; if (arr.length) setDepartments(arr); })
      .catch(() => {});
    api.get("/service-categories")
      .then((r) => { const arr = Array.isArray(r.data) ? r.data : []; if (arr.length) setServices(arr); })
      .catch(() => {});
  }, []);

  const [deptQuery, setDeptQuery] = useState("");
  const parents = departments.filter((d) => !d.parent_id);
  const q = deptQuery.trim().toLowerCase();
  const matches = (d) => {
    if (!q) return true;
    if ((d.name || "").toLowerCase().includes(q)) return true;
    const kids = departments.filter((k) => k.parent_id === d.id);
    return kids.some((k) => (k.name || "").toLowerCase().includes(q));
  };
  const filteredParents = parents.filter(matches);
  const subCount = departments.filter((d) => !!d.parent_id).length;
  const totalOpenings = parents.reduce((s, p) => s + (Number(p.open_positions) || 0), 0);

  return (
    <>
      <PageHero
        overline={t("hero_overline")}
        title={t("hero_title")}
        description={t("hero_body")}
      />

      {/* Hiring Solutions — dynamic Service Categories */}
      <section className="section-pad">
        <div className="container-page">
          <div className="grid gap-10 md:grid-cols-12 md:items-end">
            <div className="md:col-span-8">
              <div className="overline mb-3">{t("solutions_overline")}</div>
              <h2 className={H2_TITLE}>{t("solutions_title")}</h2>
              <p className="mt-4 max-w-2xl text-slate-600">{t("solutions_body")}</p>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Link to="/hire-talent" data-testid="services-cta-hire">
                <Button size="lg" className="h-12 bg-secondary px-7 text-secondary-foreground hover:bg-secondary/90">
                  {t("solutions_cta_label")} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-3" data-testid="service-categories">
            {services.map((svc, i) => {
              const Icon = getIcon(svc.icon, "Users");
              const roles = String(svc.roles || "").split(",").map((r) => r.trim()).filter(Boolean);
              return (
                <Reveal
                  key={svc.id || svc.title}
                  variant="up"
                  delay={i * 100}
                  data-testid={`service-${(svc.title || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-7 hover-lift hover:border-primary/40 hover:shadow-lg"
                >
                  <div className="mb-4 flex items-start justify-between">
                    <div className="inline-grid h-14 w-14 place-items-center rounded-xl bg-primary/10 text-primary transition-transform group-hover:scale-110">
                      <Icon className="h-7 w-7" />
                    </div>
                    <span className="text-xs font-semibold text-slate-300">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900">{svc.title}</h3>
                  {svc.tagline && <div className="mt-1 text-sm font-medium text-secondary">{svc.tagline}</div>}
                  <p className="mt-4 text-sm leading-relaxed text-slate-600">{svc.description}</p>
                  {roles.length > 0 && (
                    <div className="mt-5 border-t border-slate-100 pt-4">
                      <div className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-slate-500">Roles &amp; positions covered</div>
                      <div className="flex flex-wrap gap-1.5">
                        {roles.map((role) => (
                          <span key={role} className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-700">
                            {role}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Functional Domains — hierarchical list of departments + sub-departments */}
      <section className="section-pad bg-gradient-to-b from-slate-50 to-white" id="departments">
        <div className="container-page">
          <div className="grid gap-8 md:grid-cols-12 md:items-end">
            <div className="md:col-span-8">
              <div className="overline mb-3">{t("domains_overline")}</div>
              <h2 className={H2_TITLE}>{t("domains_title")}</h2>
              <p className="mt-4 max-w-2xl text-slate-600">{t("domains_body")}</p>
            </div>
            <div className="md:col-span-4">
              <div className="flex flex-wrap items-end justify-end gap-3">
                <div className="rounded-xl border border-primary/20 bg-white px-4 py-3 text-center shadow-sm">
                  <div className="font-heading text-2xl font-bold text-primary" data-testid="domain-stat-parents">{parents.length}</div>
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Departments</div>
                </div>
                <div className="rounded-xl border border-secondary/40 bg-white px-4 py-3 text-center shadow-sm">
                  <div className="font-heading text-2xl font-bold text-secondary" data-testid="domain-stat-subs">{subCount}</div>
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Sub-functions</div>
                </div>
                {totalOpenings > 0 && (
                  <div className="rounded-xl border border-emerald-200 bg-white px-4 py-3 text-center shadow-sm">
                    <div className="font-heading text-2xl font-bold text-emerald-600" data-testid="domain-stat-openings">{totalOpenings}</div>
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Open roles</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="mt-8 flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-2 shadow-sm md:max-w-md">
            <Search className="ml-2 h-4 w-4 text-slate-400" />
            <Input
              value={deptQuery}
              onChange={(e) => setDeptQuery(e.target.value)}
              placeholder="Search functions & sub-functions…"
              className="border-0 focus-visible:ring-0"
              data-testid="departments-search"
            />
            {deptQuery && (
              <button type="button" onClick={() => setDeptQuery("")} className="mr-2 text-xs font-semibold text-slate-500 hover:text-slate-800" data-testid="departments-search-clear">Clear</button>
            )}
          </div>

          {/* Card GRID — each card is a department with a LIST of sub-departments inside */}
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="domain-tree">
            {filteredParents.map((d, i) => {
              const Icon = getIcon(d.icon, "Building2");
              const label = d.name || d.label;
              const kids = departments.filter((k) => k.parent_id === d.id);
              const visibleKids = q
                ? kids.filter((k) => (k.name || "").toLowerCase().includes(q) || (label || "").toLowerCase().includes(q))
                : kids;
              const open = Number(d.open_positions) || 0;
              return (
                <div
                  key={d.id || label}
                  data-testid={`domain-${d.id || i + 1}`}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md"
                >
                  {/* Card header — parent department */}
                  <div className="flex items-start gap-3 border-b border-slate-100 bg-slate-50/60 p-4">
                    <div className="inline-grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 text-primary transition-transform group-hover:scale-105">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-heading text-base font-semibold leading-tight text-slate-900">{label}</div>
                      <div className="mt-0.5 text-[11px] font-medium uppercase tracking-widest text-slate-400">
                        {kids.length} sub-function{kids.length === 1 ? "" : "s"}
                        {open > 0 && (
                          <>
                            <span className="mx-1.5 text-slate-300">·</span>
                            <span className="text-emerald-600">{open} open</span>
                          </>
                        )}
                      </div>
                    </div>
                    {open > 0 && (
                      <Link
                        to={`/jobs?department=${encodeURIComponent(label)}`}
                        className="shrink-0 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100"
                        data-testid={`domain-open-${d.id || i + 1}`}
                      >
                        View
                      </Link>
                    )}
                  </div>

                  {/* Card body — vertical list of sub-departments */}
                  {visibleKids.length > 0 ? (
                    <ul className="flex-1 divide-y divide-slate-100">
                      {visibleKids.map((k) => {
                        const kOpen = Number(k.open_positions) || 0;
                        return (
                          <li key={k.id} data-testid={`domain-sub-row-${k.id}`}>
                            <Link
                              to={`/jobs?department=${encodeURIComponent(label)}&sub_department=${encodeURIComponent(k.name)}`}
                              data-testid={`domain-sub-${k.id}`}
                              className="flex items-start gap-2 px-4 py-2.5 text-sm text-slate-700 transition-colors hover:bg-primary/5 hover:text-primary"
                            >
                              <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                              <span className="min-w-0 flex-1 leading-snug break-words">{k.name}</span>
                              {kOpen > 0 && (
                                <span className="mt-0.5 shrink-0 rounded-full bg-secondary/20 px-1.5 text-[10px] font-semibold text-secondary-foreground/70">
                                  {kOpen}
                                </span>
                              )}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <div className="flex-1 px-4 py-4 text-xs text-slate-400 italic">No sub-functions yet</div>
                  )}

                  {open > 0 && (
                    <Link
                      to={`/jobs?department=${encodeURIComponent(label)}`}
                      className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-sm font-semibold text-primary transition-colors hover:bg-primary/5"
                      data-testid={`domain-cta-${d.id || i + 1}`}
                    >
                      View {open} opening{open === 1 ? "" : "s"}
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  )}
                </div>
              );
            })}
            {filteredParents.length === 0 && (
              <div className="col-span-full rounded-xl border border-dashed border-slate-300 p-10 text-center text-slate-500" data-testid="departments-empty">
                <Building2 className="mx-auto mb-2 h-8 w-8 text-slate-300" />
                No functions match &ldquo;<span className="font-semibold text-slate-700">{deptQuery}</span>&rdquo;. Try a different keyword.
              </div>
            )}
          </div>

          <div className="mt-10 rounded-xl border border-primary/20 bg-primary/5 p-5 text-center">
            <div className="text-sm text-slate-700">
              <Check className="mr-2 inline h-4 w-4 text-primary" />
              {t("domains_footer")}{" "}
              <Link to="/hire-talent" className="font-semibold text-primary hover:underline">Talk to us</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-primary text-primary-foreground">
        <div className="container-page grid gap-6 py-14 md:grid-cols-12 md:items-center">
          <div className="md:col-span-8">
            <h2 className="font-heading text-[32px] font-semibold sm:text-[38px] leading-tight">{t("final_cta_title")}</h2>
            <p className="mt-2 text-white/85">{t("final_cta_body")}</p>
          </div>
          <div className="flex flex-wrap gap-3 md:col-span-4 md:justify-end">
            <Link to="/hire-talent" data-testid="services-bottom-hire">
              <Button size="lg" className="h-12 bg-secondary text-secondary-foreground hover:bg-secondary/90">{settings?.hire_talent_button_text || "Hire Talent"}</Button>
            </Link>
            <Link to="/jobs" data-testid="services-bottom-jobs">
              <Button size="lg" variant="outline" className="h-12 border-white text-white hover:bg-white/10">Browse Openings</Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
